import {Component, ElementRef, Input, OnInit, Output, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {EventEmitter} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';

import {of} from 'rxjs';
import {catchError, finalize, switchMap, take, tap} from 'rxjs/operators';

import {ToastrService} from 'ngx-toastr';
import {User, UserService} from '../../shared/user';
import {capitalize} from '../../shared/utils/strings';
import {AiResponse, ChatMessage, ResponseType, SafeChartOption, Sender, SeriesType} from '../chatbot.interface';
import {ChatbotService} from '../chatbot.service';

@Component({
  selector: 'app-widget-chatbot',
  templateUrl: './widget-chatbot.component.html',
  styleUrls: ['./widget-chatbot.component.scss']
})
export class WidgetChatbotComponent implements OnInit {
  public isMaximized = false;
  public readonly Sender = Sender;
  public readonly ResponseType = ResponseType;
  public messages: ChatMessage[] = [];
  public newMessage = new UntypedFormControl('');
  public isRequestInProgress = false;
  public showNewChatButton = false;
  public isFeedbackMode = false;
  public isDiscardConfirmed = false;
  public currentRecommendations: string[] = [];
  public areRecommendationsExpanded = true;

  public chatIconSrc = 'assets/icon/favicon-32x32.png';
  public productName = 'SD Worx People';
  public chatbotName = 'SD Worx Assist';
  public chatIconAlt = `${this.productName} icon`;
  public showDisclaimer = true;
  public isAtChatBottom = true;

  @Output()
  public toggle = new EventEmitter<void>();

  @Input()
  public open = false;

  @ViewChild('chatInput')
  public chatInput: ElementRef<HTMLTextAreaElement>;

  @ViewChild('scrollContainer')
  public scrollContainer!: ElementRef<HTMLDivElement>;

  @ViewChildren('messageItem')
  public messageItems!: QueryList<ElementRef<HTMLElement>>;

  constructor(
    private chatBotService: ChatbotService,
    private userService: UserService,
    private translate: TranslateService,
    private toastr: ToastrService
  ) {
    this.translate.onLangChange.pipe(
      switchMap(() => this.userService.getUser()),
      tap((user: User) => this.personalizeWelcomeMessage(user))
    ).subscribe();

  }

  public ngOnInit() {
    if (this.showDisclaimer) {
      this.newMessage.disable({ emitEvent: false });
    }

    this.userService.getUser().pipe(
      tap((user: User) => this.personalizeWelcomeMessage(user))
    ).subscribe();
  }

  public toggleChatbot() {
    this.toggle.emit();
  }

  public toggleMaximize() {
    this.isMaximized = !this.isMaximized;
  }

  public sendMessage() {
    if (this.showDisclaimer || this.isRequestInProgress) { return; }

    const msg = this.newMessage.value.trim();
    if (!msg || this.isRequestInProgress) {
      return;
    }

    this.messages.push({text: msg, sender: Sender.USER});
    this.newMessage.setValue('');
    this.resetTextareaHeight();

    setTimeout(() => this.scrollToBottom(), 0);

    const loadingMessage: ChatMessage = {
      text: 'Loading, Great things take time!',
      sender: Sender.BOT,
      loading: true
    };
    this.messages.push(loadingMessage);

    this.isRequestInProgress = true;

    this.chatBotService.sendProcessQuery(msg)
      .pipe(
        switchMap(response => {
          if (response?.responseType === ResponseType.CHART && response?.chartContent) {
            const type = this.inferChartType(response.chartContent);
            if (type !== 'pie' && type !== 'bar') {
              this.messages.pop();
              this.messages.push({
                text: this.translate.instant('CHATBOT.graph_error_message'),
                sender: Sender.BOT
              });
              return of(null);
            }
          }
          this.handleResponse(response);
          return of(null);
        }),
        catchError(() => {
          this.messages.pop();
          this.messages.push({
            text: 'Something went wrong. Please try again!',
            sender: Sender.BOT
          });
          return of(null);
        }),
        finalize(() => {
          this.isRequestInProgress = false;
        })
      )
      .subscribe();
  }

  public startFeedbackMode(): void {
    if (this.showDisclaimer || this.isRequestInProgress) { return; }
    this.isFeedbackMode = true;
    this.isDiscardConfirmed = false;
    this.newMessage.setValue('');
    setTimeout(() => {
      this.chatInput?.nativeElement?.focus();
      this.adjustTextareaHeight();
    });
  }

  public onDiscardClicked(): void {
    const hasText = !!this.newMessage.value?.trim();
    if (hasText) {
      this.isDiscardConfirmed = true;
    } else {
      this.confirmDiscard();
    }
  }

  public keepEditing(): void {
    this.isDiscardConfirmed = false;
  }

  public confirmDiscard(): void {
    this.newMessage.setValue('');
    this.resetTextareaHeight();
    this.isDiscardConfirmed = false;
    this.isFeedbackMode = false;
  }

  public sendFeedback(): void {
    if (this.showDisclaimer || this.isRequestInProgress) { return; }

    const feedback = (this.newMessage.value || '').trim();
    if (!feedback) { return; }

    this.newMessage.setValue('');
    this.resetTextareaHeight();
    this.isFeedbackMode = false;
    this.isDiscardConfirmed = false;

    this.isRequestInProgress = true;

    this.chatBotService.sendFeedback(feedback)
      .pipe(
        tap(res => {
          if (res) {
            this.toastr.success(this.translate.instant('CHATBOT_FEEDBACK.feedback_sent_successfully'), null, {
              positionClass: 'toast-bottom-right',
              timeOut: 3000
            });
          }
        }),
        catchError(() => {
          return of(null);
        }),
        finalize(() => {
          this.isRequestInProgress = false;
        })
      )
      .subscribe();
  }

  public handleResponse(response: AiResponse): void {
    this.messages.pop();

    let safeChart: SafeChartOption = null;

    if (response.responseType === ResponseType.CHART && response.chartContent) {
      try {
        safeChart = this.buildSafeChartOption(response.chartContent);
      } catch (e) {
        safeChart = null;
      }
    }

    this.currentRecommendations = response.recommendations || [];

    const newBotMessage: ChatMessage = {
      text: response.answer,
      sender: Sender.BOT,
      responseType: response.responseType,
      chartContent: safeChart ?? undefined,
    };

    this.messages.push(newBotMessage);
    this.showNewChatButton = !!this.messages[2];
    this.handleChatScroll();
  }

  public acceptDisclaimer(): void {
    this.showDisclaimer = false;
    this.newMessage.enable({ emitEvent: false });
  }

  public refuseDisclaimer(): void {
    this.toggleChatbot();
    this.showDisclaimer = true;
    this.newMessage.disable({ emitEvent: false });
  }

  public newChat() {
    if (this.isRequestInProgress) {
      return;
    }
    this.isRequestInProgress = true;
    this.chatBotService.newChat();
    this.currentRecommendations = [];

    this.userService.getUser().pipe(
      take(1),
      tap(user => {
        this.messages = [];
        this.personalizeWelcomeMessage(user);
      }),
      finalize(() => {
        this.isRequestInProgress = false;
        this.showNewChatButton = false;
        this.isAtChatBottom = true;
      }),
      catchError(error => {
        console.error('Failed to start new chat:', error);
        return of(null);
      })
    ).subscribe();
  }

  public onRecommendationClicked(recommendation: string) {
    this.newMessage.setValue(recommendation);
    this.sendMessage();
    this.adjustTextareaHeight();
  }

  public adjustTextareaHeight() {
    if (this.chatInput) {
      const textarea = this.chatInput.nativeElement;
      textarea.style.height = '60px';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  }

  public getBoldText(text: string): string {
    return `<strong>${text}</strong>`;
  }

  public scrollToBottom(): void {
    try {
      this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
    } catch (err) {
      console.warn('Scroll failed', err);
    } finally {
      this.isAtChatBottom = true;
    }
  }

  public onChatScroll(): void {
    const element = this.scrollContainer?.nativeElement;
    if (!element) { return; }

    const threshold = 10; // distance from the bottom to consider as "at bottom"
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    this.isAtChatBottom = distanceFromBottom <= threshold;
  }

  private resetTextareaHeight() {
    if (this.chatInput) {
      this.chatInput.nativeElement.style.height = '60px';
    }
  }

  private wrapLabel(text: string, maxLineChars: number): string {
    if (!text || text.length <= maxLineChars) {
      return text;
    }

    const lines: string[] = [];
    let current = '';

    for (const word of text.split(' ')) {
      if ((current + word).length > maxLineChars) {
        lines.push(current.trim());
        current = '';
      }
      current += `${word} `;
    }

    if (current) {
      lines.push(current.trim());
    }

    return lines.join('\n');
  }

  private personalizeWelcomeMessage(user: User): void {
    const translatedText = this.translate.instant('CHATBOT.welcome_message', {
      name: capitalize(user.firstName)
    });

    this.messages = [
      {
        text: translatedText,
        sender: Sender.BOT
      },
      ...this.messages.slice(1)
    ];
  }

  public getChartWidth(opt: SafeChartOption | undefined): string {
    if (!opt) return '1200px';

    if (!Array.isArray(opt?.series) || opt.series.length === 0) {
      return '1200px';
    }

    if (opt.series.some((s: any) => s?.type === 'bar')) {
      const xa = opt?.xAxis;
      const xData: string[] | undefined = Array.isArray(xa)
        ? (Array.isArray(xa[0]?.data) ? xa[0].data : undefined)
        : (Array.isArray(xa?.data) ? xa.data : undefined);

      const labelCount =
        (Array.isArray(xData) && xData.length) ? xData.length
          : (Array.isArray(opt.series[0]?.data) ? opt.series[0].data.length : 10);

      const px = Math.max(800, Math.min(2600, labelCount * 80));
      return `${px}px`;
    }

    if (opt.series.some((s: any) => s?.type === 'pie')) {
      const firstPie = opt.series.find((s: any) => s?.type === 'pie');
      const sliceCount = Array.isArray(firstPie?.data) ? firstPie.data.length : 0;
      const px = (900 + (sliceCount - 15) * 12);
      return `${px}px`;
    }

    return '1200px';
  }

  private buildSafeChartOption(raw: any): SafeChartOption | null {
    if (!raw) return null;

    const type = this.inferChartType(raw);
    if (!type) return null;

    switch (type) {
      case 'pie':
        return this.buildPieOption(raw);
      case 'bar':
        return this.buildBarOption(raw);
      default:
        return null;
    }
  }

  // Pie chart
  private buildPieOption(raw): SafeChartOption {
    const pieSeriesList = raw.series.filter(s => s?.type === 'pie');

    // 1 pie chart
    if (pieSeriesList.length <= 1) {
      const pieSeries = pieSeriesList[0];

      const src: [] =
        (Array.isArray(pieSeries?.data) ? pieSeries.data : undefined) ??
        (Array.isArray(raw?.data) ? raw.data : []) ?? [];

      const pieData = src
        .map((d: any) => ({
          name: this.wrapLabel(String(d?.name ?? ''), 30),
          value: Number(d?.value),
        }))
        .filter(d => d.name && Number.isFinite(d.value) && d.value >= 0);

      const sliceCount = pieData.length;
      const needsTightLayout = sliceCount > 15
      const centerX = needsTightLayout ? ((sliceCount/15)*270) : '50%';

      return {
        title: raw?.title?.text ? { text: String(raw.title.text) } : undefined,
        tooltip: { trigger: 'item' },
        legend: {
          top: 30,
          left: 'left',
          orient: 'vertical',
          data: pieData.map(d => d.name),
        },
        series: [
          {
            type: 'pie',
            data: pieData,
            ...(needsTightLayout ? { center: [centerX, '50%'], radius: '40%' } : {}),
            label: { normal: { show: false }, emphasis: { show: false } },
            labelLine: { normal: { show: false }, emphasis: { show: false } },
          } as any,
        ],
      } as SafeChartOption;
    }

    // More than 1 pie chart
    const sanitizeData = (src: []) =>
      (Array.isArray(src) ? src : [])
        .map(d => ({
          name: this.wrapLabel(String(d?.name ?? ''), 30),
          value: Number(d?.value),
        }))
        .filter(d => d.name && Number.isFinite(d.value) && d.value >= 0);

    const centerFor = (idx: number): [string, string] => {
      const laneX = ['25%', '50%', '75%'];
      const x = laneX[Math.min(idx, laneX.length - 1)];
      return [x, '60%'];
    };

    const total = pieSeriesList.length;
    const exactlyTwo = total === 2;

    const builtSeries = pieSeriesList.map((s, idx: number) => {
      const data = sanitizeData(s?.data);

      const center: [string, string] = exactlyTwo
        ? (idx === 0 ? ['40%', '50%'] : ['65%', '50%'])
        : (s?.center ?? centerFor(idx));

      const radius: string = exactlyTwo
        ? '50%'
        : (typeof s?.radius === 'string' ? s.radius : '50%');

      return {
        type: 'pie',
        name: s?.name ? String(s.name) : undefined,
        data,
        radius,
        center,
        label: { normal: { show: false }, emphasis: { show: false } },
        labelLine: { normal: { show: false }, emphasis: { show: false } },
        itemStyle: s?.itemStyle ?? undefined,
        emphasis: s?.emphasis ?? undefined,
      };
    });

    const inferredLegend = Array.from(
      new Set(builtSeries.flatMap(s => (s.data || []).map(d => d.name)))
    );

    return {
      title: raw?.title?.text ? { text: String(raw.title.text) } : undefined,
      tooltip: { trigger: 'item' },
      legend: raw?.legend
        ? { ...raw.legend, left: 'left', orient: 'vertical' }
        : { top: 30, left: 'left', orient: 'vertical', data: inferredLegend },
      series: builtSeries,
    } as SafeChartOption;
  }

  // Bar chart
  private buildBarOption(raw): SafeChartOption {
    const seriesArr: any[] = Array.isArray(raw?.series) ? raw.series : [];

    const xAxisAny = raw?.xAxis;
    const xDataFromXAxis: string[] | undefined = Array.isArray(xAxisAny)
      ? (Array.isArray(xAxisAny[0]?.data) ? (xAxisAny[0].data as string[]) : undefined)
      : (Array.isArray(xAxisAny?.data) ? (xAxisAny.data as string[]) : undefined);

    const labelCandidate: string[] =
      xDataFromXAxis ??
      (Array.isArray(raw?.labels) ? (raw.labels as string[]) : undefined) ??
      [];

    const labels = labelCandidate.map(v => this.wrapLabel(String(v ?? ''), 25));

    const chosen = seriesArr.some(s => s?.type === 'bar')
      ? seriesArr.filter(s => s?.type === 'bar')
      : seriesArr;

    const series = chosen
      .map(s => {
        const src = Array.isArray(s?.data) ? s.data : [];

        const data = src.map(item => {
          if (item == null) return null;
          if (typeof item === 'object' && 'value' in item) {
            const v = Number(item.value);
            if (Number.isFinite(v)) {
              return { ...item, value: v };
            }
            return null;
          }
          const v = Number(item);
          return Number.isFinite(v) ? v : null;
        });

        return {
          type: 'bar',
          name: s?.name ? String(s.name) : undefined,
          itemStyle: s?.itemStyle,
          data,
        };
      })
      .filter(s => Array.isArray(s.data) && s.data.length > 0);

    const allVals: number[] = series.flatMap(s =>
      (s.data as any[]).map(d => (d && typeof d === 'object' && 'value' in d ? Number(d.value) : Number(d)))
    ).filter(n => Number.isFinite(n));

    const maxY = allVals.length ? Math.ceil(Math.max(...allVals) * 1.1) : undefined;

    const tooltip = raw?.tooltip && typeof raw.tooltip.formatter === 'function'
      ? raw.tooltip
      : { trigger: 'axis' };

    return {
      title: raw?.title?.text ? { text: String(raw.title.text) } : undefined,
      tooltip,
      legend: raw?.legend,
      grid: raw?.grid ?? { top: 40, bottom: 100, containLabel: true },
      xAxis: { type: 'category', data: labels, axisLabel: { rotate: 45, interval: 0, margin: 20 } },
      yAxis: { type: 'value', min: 0, max: maxY },
      series: series as any,
    };
  }


  private inferChartType(raw): SeriesType {
    const seriesArr = Array.isArray(raw.series) ? raw.series : [];
    const types = new Set(seriesArr.map(s => s?.type));
    if (types.has('pie')) return 'pie';
    if (types.has('bar')) return 'bar';
    if (raw.type === 'pie' || raw.type === 'bar') return raw.type as SeriesType;
    return null;
  }

  public handleChatScroll(): void {
    setTimeout(() => {
      const container = this.scrollContainer?.nativeElement;
      const lastBotElement = this.messageItems?.toArray().reverse().find(element =>
        element.nativeElement.classList.contains('bot')
      )?.nativeElement;

      if (container && lastBotElement) {
        container.scrollTop = lastBotElement.offsetTop;
        this.onChatScroll();
      }
    }, 0);
  }

  public toggleRecommendations(): void {
    this.areRecommendationsExpanded = !this.areRecommendationsExpanded;
  }
}
